import { Grid } from "@mui/material";
import BreadCrumbComp from "../../components/breadCrumb";
import SimpleSelect from "../../components/selects/simpleSelect";
import { icons } from "../../constants";
import "./styles.css";
import { useForm } from "react-hook-form";
import { getCurrentMonth, getCurrentYear, getMonthName } from "../../utils/dateUtils";
import { months } from "../../constants/selectData";
import { buildOpenClosedSelect } from "../../services/dateService";
import ExcelUploadField from "../../components/excelUploadField";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import LaddaButton from "../../components/buttons/laddaButton";
import SimpleTable from "../../components/tables/simpleTable";
import { walletColumns as baseWalletColumns } from "./tableColumns";
import VerificationModal from "../../components/modals/verificationModal";
import WalletProductErrorsModal from "../../components/modals/walletProductErrorsModal";
// import WalletAutoInsertedModal from "../../components/modals/walletAutoInsertedModal"; // Modal informativo REDPINE: desactivado por ahora
import { useSelector } from "react-redux";
import ToastNotification from "../../components/toastNotification";
import { canManageWallet } from "../../constants/walletRoles";
import { downloadWalletZip } from "../../services/apiService";
import {
  postWalletUploadDATA,
  getWalletHistoryDATA,
  getWalletLinesDATA,
  walletPreflightDATA
} from "./walletCallApi";
import {
  extractWalletUploadError,
  normalizeWalletUploadErrorForModal,
  shouldOpenWalletUploadErrorModal
} from "./walletUploadErrorUtils";

const Wallet = () => {
  const [carteraMesActualFile, setCarteraMesActualFile] = useState(null);
  const [ventasAnoActualFile, setVentasAnoActualFile] = useState(null);
  const [ventasAnoAnteriorFile, setVentasAnoAnteriorFile] = useState(null);
  const [uploadKey, setUploadKey] = useState(0);
  const [openVerification, setOpenVerification] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [verificationText, setVerificationText] = useState(
    "¿Estás seguro de generar la cartera con los archivos seleccionados?"
  );
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastSeverity, setToastSeverity] = useState("success"); // success | error | warn | info
  const [toastMessage, setToastMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [lineOptions, setLineOptions] = useState([{ name: "Todas", value: "null" }]);
  const [productErrorsModalOpen, setProductErrorsModalOpen] = useState(false);
  const [productErrorsData, setProductErrorsData] = useState(null);
  // Modal informativo "productos insertados como REDPINE" — desactivado por ahora
  // const [autoInsertedModalOpen, setAutoInsertedModalOpen] = useState(false);
  // const [autoInsertedData, setAutoInsertedData] = useState([]);
  const urlApi = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const { control, handleSubmit, getValues, watch } = useForm({
    defaultValues: {
      year: getCurrentYear(),
      month: getCurrentMonth(),
      historyYear: getCurrentYear(),
      historyMonth: getCurrentMonth(),
      historyLine: "null"
    }
  });

  const historyYear = watch("historyYear");
  const historyMonth = watch("historyMonth");
  const historyLine = watch("historyLine");

  const userData = useSelector((state) => state.userData.userData);
  const isWalletAdmin = canManageWallet(userData.roleList);

  useEffect(() => {
    setHistoryPage(1);
  }, [historyYear, historyMonth, historyLine]);

  useEffect(() => {
    getWalletHistoryDATA(
      historyYear,
      historyMonth,
      historyLine,
      historyPage,
      10,
      setTableData,
      setHistoryTotal,
      setHistoryPage,
      userData.id
    );
  }, [historyYear, historyMonth, historyLine, historyPage]);

  useEffect(() => {
    getWalletLinesDATA(setLineOptions, userData.id);
  }, []);

  const resetUploadForm = () => {
    setCarteraMesActualFile(null);
    setVentasAnoActualFile(null);
    setVentasAnoAnteriorFile(null);
    setUploadKey((k) => k + 1); // remount inputs
  };

  const showSuccess = (msg) => {
    setSubmitError("");
    setSubmitSuccess(msg);
    setToastSeverity("success");
    setToastMessage(msg);
    setShowToast(true);
  };

  const showError = (msg) => {
    setSubmitSuccess("");
    setSubmitError(msg);
    setToastSeverity("error");
    setToastMessage(msg);
    setShowToast(true);
  };

  const triggerDownloadBlob = (blob, fileName = "wallet.zip") => {
    const isAndroid = /Android/i.test(navigator.userAgent);

    const blobUrl = window.URL.createObjectURL(blob);

    if (isAndroid) {
      window.open(blobUrl, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 15000);
      return;
    }

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);
  };

  const handleDownloadZip = async (row) => {
    if (!row?.id) {
      showError("No se pudo descargar: falta el id del registro.");
      return;
    }

    const monthLabel = getMonthName(Number(row.month));
    const lineLabel = row?.lineName || row?.businessLine || "LINEA";
    const zipName = `WALLET_${monthLabel}_${row.year}_${lineLabel}.zip`
      .replace(/\s+/g, "_")
      .toUpperCase();

    try {
      const resp = await downloadWalletZip(row.id, userData.id);
      const blob = resp?.data instanceof Blob ? resp.data : resp;
      if (!(blob instanceof Blob)) {
        showError("No se pudo descargar el ZIP (respuesta inválida).");
        return;
      }
      triggerDownloadBlob(blob, zipName);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        "No se pudo descargar el ZIP. Intenta de nuevo.";
      showError(msg);
    }
  };

  const onSubmit = async (formData) => {
    setSubmitError("");
    setSubmitSuccess("");

    const missing = [];
    if (!formData.year) missing.push("Año");
    if (!formData.month) missing.push("Mes");
    if (!carteraMesActualFile) missing.push("Cartera Mes actual (Excel)");
    if (!ventasAnoActualFile) missing.push("Ventas Año actual (Excel)");

    if (missing.length) {
      showError(`Faltan campos obligatorios: ${missing.join(", ")}`);
      return;
    }

    const filesPayload = {
      carteraMesActual: carteraMesActualFile,
      ventasAnoActual: ventasAnoActualFile
    };
    if (ventasAnoAnteriorFile) filesPayload.ventasAnoAnterior = ventasAnoAnteriorFile;

    const payload = {
      year: formData.year,
      month: formData.month,
      businessLine: "SerAnest",
      documentType: "Cartera mensual",
      files: filesPayload
    };

    await walletPreflightDATA(
      { year: formData.year, month: formData.month },
      (pf) => {
        const monthName = getMonthName(Number(formData.month));
        if (pf?.mode === "update" || pf?.mode === "mixed") {
          setVerificationText(
            `⚠️ Ya existen carteras para ${monthName} ${formData.year}. ` +
              `Si continúas, se reemplazarán/actualizarán los archivos existentes. ¿Deseas continuar?`
          );
        } else {
          setVerificationText(
            "¿Estás seguro de generar la cartera con los archivos seleccionados?"
          );
        }

        setPendingPayload(payload);
        setOpenVerification(true);
      },
      userData.id
    );
  };

  const handleConfirmGenerate = async () => {
    if (!pendingPayload || isSubmitting) {
      setOpenVerification(false);
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setSubmitSuccess("");

    await postWalletUploadDATA(
      { ...pendingPayload, userId: userData.id },
      (dataResp) => {
        const data = dataResp?.data ?? dataResp;

        const monthName = getMonthName(Number(pendingPayload.month));
        const year = pendingPayload.year;

        const createdLinesText =
          Array.isArray(data?.lines) && data.lines.length
            ? ` Líneas: ${data.lines
                .map((l) => l.lineName || l.line_name || l.lineId || l.line_id)
                .join(", ")}.`
            : "";

        const ws = data?.walletSales;
        let walletSalesText = "";
        if (ws?.ok && Number(ws.inserted) >= 0) {
          walletSalesText = ` Ventas en BD (wallet_sales): ${ws.inserted} filas del año ${ws.fileYear ?? year}.`;
        } else if (ws && ws.ok === false) {
          walletSalesText = ` No se importaron ventas a wallet_sales: ${ws.error || ws.hint || "revisa la tabla en BD"}.`;
        }

        const successMsg = `Cartera ${monthName} ${year} generada y cargada con éxito.${createdLinesText}${walletSalesText}`;
        showSuccess(successMsg);
        setSubmitSuccess(successMsg);
        // Modal informativo REDPINE desactivado: if (Array.isArray(data?.autoInserted) && data.autoInserted.length > 0) {
        //   setAutoInsertedData(data.autoInserted);
        //   setAutoInsertedModalOpen(true);
        // }
        const values = getValues();
        getWalletHistoryDATA(
          values.historyYear,
          values.historyMonth,
          values.historyLine ?? "null",
          1,
          10,
          setTableData,
          setHistoryTotal,
          setHistoryPage,
          userData.id
        );
        setOpenVerification(false);
        setPendingPayload(null);
        resetUploadForm();

        setIsSubmitting(false);
      },
      (err) => {
        setIsSubmitting(false);

        const { data, message } = extractWalletUploadError(err);
        showError(message);

        const normalized = normalizeWalletUploadErrorForModal(data);
        if (shouldOpenWalletUploadErrorModal(normalized)) {
          setProductErrorsData(normalized);
          setProductErrorsModalOpen(true);
        }

        setOpenVerification(false);
        setPendingPayload(null);
      }
    );
  };

  const handleCloseModal = () => {
    if (isSubmitting) return;
    setOpenVerification(false);
    setPendingPayload(null);
  };

  const walletColumns = useMemo(() => {
    return baseWalletColumns.map((col) => {
      if (col.field !== "files") return col;

      return {
        ...col,
        render: (row) => (
          <div className="wallet-file-chips">
            {(row.files || []).map((file, idx) => (
              <span
                key={idx}
                className="wallet-file-chip"
                title={file.name}
                onClick={() => {
                  if (!file?.url || file.url === "#") {
                    showError("Este archivo no tiene URL disponible para descarga.");
                    return;
                  }
                  // url del back viene como "/files/..."
                  const cleanedPath = String(file.url).replace(/\\/g, "/");
                  const fullUrl = cleanedPath.startsWith("http")
                    ? cleanedPath
                    : `${urlApi}${cleanedPath}`;

                  window.open(fullUrl, "_blank");
                }}
              >
                {file.shortLabel}
              </span>
            ))}
          </div>
        )
      };
    });
  }, [urlApi]);

  return (
    <>
      <ToastNotification
        severity={toastSeverity}
        summary={toastMessage}
        detail=""
        position="top-center"
        showToast={showToast}
        setShowToast={setShowToast}
      />

      <BreadCrumbComp />
      <div className="searchTitle">
        <h1>CARTERA</h1>
      </div>

      {isWalletAdmin && (
        <div className="table-container">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap"
            }}
          >
            <h1 className="title-bio-details-chard" style={{ margin: 0 }}>
              Cartera SerAnest
            </h1>
            <LaddaButton
              variant="contained"
              onClick={() => navigate("/wallet/products")}
            >
              <div className="HContainer align-center">
                <img className="btn-icon mr-10" src={icons.gear} alt="icon" />
                <p className="button_text">Administrar productos</p>
              </div>
            </LaddaButton>
          </div>
          <p>Selecciona el Mes y Año al que corresponde la Cartera</p>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <p className="blueLabelForm">Año:</p>
              <SimpleSelect
                margin={"0"}
                name="year"
                control={control}
                options={buildOpenClosedSelect()}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <p className="blueLabelForm">Mes:</p>
              <SimpleSelect
                margin={"0"}
                name="month"
                control={control}
                options={months}
              />
            </Grid>
          </Grid>

          <h1 className="title-bio-details-chard">Carga de Documentos</h1>
          <p>Haz clic en «Seleccionar archivo» para cargar los documentos</p>

          <div className="info-card">
            <img className="btn-icon-32 mr-10" src={icons.info_blue} alt="icon" />
            <span>
              Para calcular la cartera del mes actual debes subir los documentos requeridos.
              Una vez cargados, podrás visualizar y descargarlos.
            </span>
          </div>

          <br />
          <Grid container spacing={2} key={uploadKey}>
            <Grid item xs={12}>
              <ExcelUploadField
                label="Cartera Mes actual"
                onFileChange={(file) => setCarteraMesActualFile(file)}
                disabled={isSubmitting}
              />
            </Grid>

            <Grid item xs={12}>
              <ExcelUploadField
                label="Ventas Año actual"
                onFileChange={(file) => setVentasAnoActualFile(file)}
                disabled={isSubmitting}
              />
            </Grid>

            <Grid item xs={12}>
              <ExcelUploadField
                label="Ventas año anterior (opcional)"
                onFileChange={(file) => setVentasAnoAnteriorFile(file)}
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>
          {submitError ? (
            <div
              style={{
                marginTop: "14px",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid rgba(198,40,40,.35)",
                background: "rgba(198,40,40,.08)",
                color: "#c62828",
                fontSize: "13px"
              }}
            >
              {submitError}
            </div>
          ) : null}

          {submitSuccess ? (
            <div
              style={{
                marginTop: "14px",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid rgba(46,125,50,.35)",
                background: "rgba(46,125,50,.08)",
                color: "#2e7d32",
                fontSize: "13px"
              }}
            >
              {submitSuccess}
            </div>
          ) : null}

          <div className="HContainer j-center w-complete" style={{ marginTop: "16px" }}>
            <LaddaButton
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting || openVerification}
            >
              <div className="HContainer align-center">
                <p className="button_text">
                  {isSubmitting ? "Procesando cartera..." : "Generar Cartera"}
                </p>
              </div>
            </LaddaButton>
          </div>
        </div>
      )}

      <div className="table-container">
        <h1 className="title-bio-details-chard">Historial Cartera</h1>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <p className="blueLabelForm">Año:</p>
            <SimpleSelect
              margin={"0"}
              name="historyYear"
              control={control}
              options={buildOpenClosedSelect()}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <p className="blueLabelForm">Mes:</p>
            <SimpleSelect
              margin={"0"}
              name="historyMonth"
              control={control}
              options={months}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <p className="blueLabelForm">Línea de Negocio:</p>
            <SimpleSelect
              margin={"0"}
              name="historyLine"
              control={control}
              options={lineOptions}
            />
          </Grid>
        </Grid>

        <br />

        <SimpleTable
          data={tableData}
          columns={walletColumns}
          buttons={[
            {
              type: "icon",
              icon: icons.descargar,
              onClick: (row) => handleDownloadZip(row)
            }
          ]}
        />
      </div>

      <VerificationModal
        open={openVerification}
        handleClose={handleCloseModal}
        alertText={verificationText}
        onConfirm={handleConfirmGenerate}
        loading={isSubmitting}
      />

      <WalletProductErrorsModal
        open={productErrorsModalOpen}
        onClose={() => {
          setProductErrorsModalOpen(false);
          setProductErrorsData(null);
        }}
        errorData={productErrorsData}
      />

      {/* Modal informativo "productos insertados como REDPINE" — desactivado por ahora
      <WalletAutoInsertedModal
        open={autoInsertedModalOpen}
        onClose={() => {
          setAutoInsertedModalOpen(false);
          setAutoInsertedData([]);
        }}
        autoInserted={autoInsertedData}
      />
      */}
    </>
  );
};

export default Wallet;
